import React, { useRef, useState } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import {
  downloadPdfFromBuffer,
  injectHtmlBaseHref,
  isHTML,
  openDillToast,
} from '../../../../utils/helpers';
import { useWindowActualWidth } from '../../../../utils/domTools';
import { twMerge } from 'tailwind-merge';
import { DownloadSimple, Printer } from 'phosphor-react';

const ExperianCreditReportModal = ({
  open,
  handleClose,
  details,
  bufferData,
  experianBin,
}: {
  open: boolean;
  handleClose: () => void;
  details: any;
  bufferData?: { data: number[] } | null;
  experianBin: string;
  creditApplicationId: string;
}) => {
  const [loading] = useState(false);
  const width = useWindowActualWidth();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleCloseModal = () => {
    handleClose();
  };

  const disableHtmlInteractions = (html: string) => {
    const overlay = '<div style="position:fixed;inset:0;z-index:2147483647;"></div>';
    if (/<\/body>/i.test(html)) {
      return html.replace(/<\/body>/i, `${overlay}</body>`);
    }
    return `${html}${overlay}`;
  };

  const EXPERIAN_REFERENCE_FILES_BASE_HREF = '/experianReferenceFiles/';

  const canDownload = typeof details === 'string' && isHTML(details);

  const handleDownloadPdf = () => {
    if (!bufferData) return;
    downloadPdfFromBuffer(
      bufferData,
      `Experian-report-${experianBin || 'report'}-${Date.now()}.pdf`
    );
  };

  const handlePrintReport = () => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) {
      openDillToast({ type: 'ERROR', message: 'Report is not ready to print yet.' });
      return;
    }
    iframeWindow.focus();
    iframeWindow.print();
  };

  const renderReportContent = () => {
    if (!details) return null;
    if (canDownload) {
      return (
        <iframe
          ref={iframeRef}
          srcDoc={disableHtmlInteractions(
            injectHtmlBaseHref(details, EXPERIAN_REFERENCE_FILES_BASE_HREF)
          )}
          style={{ width: '100%', height: '80vh', border: 'none' }}
          sandbox="allow-scripts allow-same-origin allow-modals"
          title="Experian Credit Report"
        />
      );
    }
    if (typeof details === 'string') {
      return <p>{details}</p>;
    }
    return (
      <pre className="text-xs overflow-auto whitespace-pre-wrap">
        {JSON.stringify(details, null, 2)}
      </pre>
    );
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Experian Credit Report"
      maxWidth="66vw"
      footer={
        <div className="flex justify-end w-full gap-2">
          {canDownload && bufferData && (
            <DillButton
              label="Download"
              variant="secondary"
              icon={<DownloadSimple size={17} />}
              onClick={handleDownloadPdf}
              isDisabled={loading}
            />
          )}
          {canDownload && !bufferData && (
            <DillButton
              label="Download / Print"
              variant="secondary"
              icon={<Printer size={17} />}
              onClick={handlePrintReport}
              isDisabled={loading}
            />
          )}
          <DillButton
            label="Close"
            variant="secondary"
            onClick={handleCloseModal}
            isDisabled={loading}
          />
        </div>
      }>
      <div className={twMerge('flex flex-col', width > 1440 ? 'items-center' : '')}>
        {renderReportContent()}
      </div>
    </DillModal>
  );
};

export default ExperianCreditReportModal;
